import React from 'react';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { Images } from '../images/Images';
import { PromoTags } from '../promoTags/PromoTags';
import AccordionContent from '../accordionContent/AccordionContent';
import UnmountAtBreakpoint from '../../common/UnmountAtBreakpoint';
import ProductBundle from '../smartModules/ProductBundle';
import { SpecialOffers } from '@jbhi-fi/components/src/ProductDetail/SpecialOffers';
import { styles } from './LeftSide.css';
import { getPromoTags } from '@jbhi-fi/components/src/ProductCard/utils/getTags';
import { useIdPrefix } from '@jbhi-fi/components/src/IdPrefix';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { SmartModuleWrapper } from '../smartModules/SmartModuleWrapper';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { useAlternativesContext } from '@jbhi-fi/components/src/ProductAlternatives/AlternativesContextProvider';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { goToAccordion, useProductAccordion } from '../AccordionContext';
import { getPerksDealStatusFromTags } from '@jbhi-fi/utils/src/perks/getPerksDealStatusFromTags';

const LeftSide = (): JSX.Element => {
  const { specialOffers, productMetafields, shopify: product } = useShopifyProduct();
  const isDesktop = useBreakpoint('lg', 'up');
  const isMarketplaceProduct = ShopifyProductTransformer.isMarketplaceProduct({ product: product });
  const pdpTopSmartModuleSettings = window.smartModuleSettings.pdpTop;
  const { isPerksEarlyAccess, isPerksExclusiveDeal } = getPerksDealStatusFromTags(
    productMetafields.ProductTags?.AllCalculatedProductTags ?? []
  );
  const isPerksMember = window.Jbhifi.customerDetails?.isPerks;
  const { products: [algoliaProduct] = [] } = useProducts({ productSkus: [window.Product.sku.toString()] });
  const { handleViewAlternatives } = useAlternativesContext();
  const accordion = useProductAccordion();
  const isSimilarItemsEnabled =
    window.smartModuleSettings.isPdpSimilarItemsEnabled &&
    !!window.smartModuleSettings?.alternativesGoogleRecModelType &&
    !isMarketplaceProduct;

  const promoTag =
    !isPerksEarlyAccess || isPerksMember || (isPerksEarlyAccess && isPerksExclusiveDeal)
      ? (productMetafields.ProductTags?.PromoTags ?? [])
      : [];
  const elementIds = {
    leftInner: useIdPrefix('left-panel-inner'),
    accordionContentWrapper: useIdPrefix('accordion-content-wrapper'),
    accordionBackground: useIdPrefix('accordion-background'),
  };
  const handleGoToAccordionWithHeaderOffset = (id: string) => goToAccordion(id, accordion, 'headerOffset');

  const promoTags = getPromoTags(promoTag);
  return (
    <>
      <UnmountAtBreakpoint breakpoint="lg" matchRule="down">
        <div className={styles.layoutLeftInner} id={elementIds.leftInner}>
          <div className={styles.topContent}>
            {promoTags && promoTags.length > 0 && isDesktop ? <PromoTags promoTags={promoTags} /> : null}
            {isSimilarItemsEnabled ? (
              <Button
                classes={styles.similarItemsButton}
                onClick={() =>
                  handleViewAlternatives(
                    algoliaProduct,
                    window.smartModuleSettings?.pdpSimilarItemsCount,
                    'similarItems'
                  )
                }
                type="linkWithIcon"
                label="Similar items"
                icon={<Icon name="ArrowRight" />}
                dataTestId="pdp-similar-items-button"
              />
            ) : null}
          </div>
          <Images />
          {specialOffers.length > 0 ? (
            <div className={styles.specialOffersContainer}>
              <SpecialOffers offers={specialOffers} onGoToAccordion={handleGoToAccordionWithHeaderOffset} />
            </div>
          ) : null}
          {pdpTopSmartModuleSettings.isEnabled && !isMarketplaceProduct ? (
            <div className={styles.bundleContainer} data-testid="pdp-top-smart-module-container">
              <ProductBundle>
                <SmartModuleWrapper
                  settings={pdpTopSmartModuleSettings}
                  location="pdpTop"
                  isMarketplaceProduct={isMarketplaceProduct}
                />
              </ProductBundle>
            </div>
          ) : (
            <ProductBundle />
          )}
        </div>
      </UnmountAtBreakpoint>
      <div className={styles.layoutLeftInner} id={elementIds.accordionContentWrapper}>
        <AccordionContent />
        <div className={styles.accordionBackground} id={elementIds.accordionBackground}></div>
      </div>
    </>
  );
};

export default LeftSide;
